/**
 * Отклонения записи членства от роли: два персональных слоя и роль-ярлык.
 *
 * Сервер отдаёт **слои-источники**, а не вычисленный результат: роль-ярлык
 * членства, персональные разрешения сверх роли и персональные запреты.
 * Действующих прав клиент не вычисляет ни в одном виде — ни разностью
 * множеств, ни сведением двух слоёв в один, ни третьим, «итоговым» списком:
 * формулу считает сервер, и копия её здесь разошлась бы с ним молча, показав
 * работающий интерфейс с чужими правами. Старшинство запрета над разрешением
 * клиентом тоже не выражается: это правило сервера, в форме данных его нет.
 *
 * Показывается ровно то, что отдал сервер: роль и два слоя. Четвёртого поля на
 * экране нет — «кто установил отклонение» невзятая половина требования, слоя
 * аудита в контракте нет ни одной операцией, и показать его было бы неоткуда.
 *
 * Выбор права идёт из **всего** каталога: состава роли в контракте нет вовсе, и
 * отфильтровать каталог по роли значило бы этот состав вычислить. Подписи
 * выбора стоят атрибутом варианта, а не его текстом: экран обязан показывать
 * ровно те права, что пришли слоями, и подпись каталога в тексте экрана была бы
 * тем самым третьим списком.
 *
 * **Отказ действия и отказ чтения — разные исходы, и место у них разное**
 * (долг `TD-67`, решение `D188`). Чтение, не удавшееся, оставляет экран без
 * содержимого — состояние принадлежит экрану. Действие происходит на экране,
 * который уже показан: осмысленный отказ сервера — «единственного директора
 * нельзя обезоружить» — принадлежит месту действия, и уносить им весь экран
 * значит показывать поломку там, где сервер объяснил правило. Человек на
 * QA-прогоне так и прочитал: как поломку продукта.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { MemberPermissions, Permission } from '../api/contract';
import { PERMISSIONS } from '../api/generated/enums';
import { permissionStringKey, roleStringKey } from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  enumLabel,
  renderScreenState,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

/** Слои, как их отдал сервер. До ответа роль не названа, а списки пусты. */
export interface RightsLayers {
  readonly role: MemberPermissions['role'] | null;
  readonly grants: readonly Permission[];
  readonly denies: readonly Permission[];
}

export interface MemberRightsScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly membershipId: string;
}

export interface MemberRightsScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  /** Слои ответа. Третьего, вычисленного, здесь нет и быть не может. */
  readonly permissions: RightsLayers;
  /** Каталог целиком — из порождённого перечня контракта. */
  readonly offeredPermissions: readonly Permission[];
  load(): Promise<void>;
  grant(right: Permission): Promise<void>;
  revokeGrant(right: Permission): Promise<void>;
  deny(right: Permission): Promise<void>;
  revokeDeny(right: Permission): Promise<void>;
}

const EMPTY_LAYERS: RightsLayers = { role: null, grants: [], denies: [] };

export function createMemberRightsScreen(
  options: MemberRightsScreenOptions,
): MemberRightsScreen {
  const { client, strings, companyId, membershipId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const role = document.createElement('p');
  const grantsHeading = document.createElement('h2');
  const grantsList = document.createElement('ul');
  const deniesHeading = document.createElement('h2');
  const deniesList = document.createElement('ul');
  const grantForm = document.createElement('form');
  const grantChoice = document.createElement('select');
  const grantSubmit = document.createElement('button');
  const denyForm = document.createElement('form');
  const denyChoice = document.createElement('select');
  const denySubmit = document.createElement('button');
  /**
   * Место сообщения сервера — на уровне экрана, а не у поля.
   *
   * Полей с именами у этого экрана нет ни одного: право выбирается вариантом
   * каталога, и имени поля тела сервер здесь не адресует. Значит места у поля
   * не найдётся **ни одному** сообщению, и второе место — то, где сообщение
   * видно человеку, — обязано быть. Класс тот же, что у мест внутри формы: это
   * то же самое, место для текста сервера.
   */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-member-rights';
  heading.textContent = strings.rights_title;
  grantsHeading.textContent = strings.rights_grants;
  deniesHeading.textContent = strings.rights_denies;
  grantForm.className = 'form';
  denyForm.className = 'form';
  grantSubmit.type = 'submit';
  grantSubmit.className = 'action';
  grantSubmit.textContent = strings.rights_grant_add;
  denySubmit.type = 'submit';
  denySubmit.className = 'action';
  denySubmit.textContent = strings.rights_deny_add;

  /**
   * Варианты выбора: подпись стоит атрибутом `label`, а не текстом варианта.
   *
   * Так браузер показывает её человеку, а текст экрана остаётся равен тому,
   * что пришло слоями: подпись каталога, попавшая в текст, читалась бы как
   * право, которое у сотрудника есть.
   */
  function fillChoice(choice: HTMLSelectElement): void {
    for (const right of PERMISSIONS) {
      const variant = document.createElement('option');

      variant.value = right;
      variant.label = enumLabel(strings, permissionStringKey(right));
      choice.append(variant);
    }
  }

  fillChoice(grantChoice);
  fillChoice(denyChoice);
  grantForm.append(grantChoice, grantSubmit);
  denyForm.append(denyChoice, denySubmit);

  let state: ScreenState | null = 'loading';
  let layers: RightsLayers = EMPTY_LAYERS;

  function rightRow(right: Permission, remove: (chosen: Permission) => void): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const drop = document.createElement('button');

    node.className = 'right-row';
    line.textContent = enumLabel(strings, permissionStringKey(right));

    drop.type = 'button';
    drop.className = 'action action--danger';
    drop.textContent = strings.rights_remove;
    drop.addEventListener('click', () => {
      remove(right);
    });

    node.append(line, drop);

    return node;
  }

  function fillList(
    list: HTMLElement,
    rights: readonly Permission[],
    remove: (chosen: Permission) => void,
  ): void {
    if (rights.length === 0) {
      // Пустой слой — это «пусто», а не пустое место: пустота читается как
      // «раздела нет», и человек не отличит её от неотвеченного обращения.
      list.replaceChildren(renderScreenState('empty', strings));

      return;
    }

    const rows: HTMLElement[] = [];

    for (const right of rights) {
      rows.push(rightRow(right, remove));
    }

    list.replaceChildren(...rows);
  }

  function render(): void {
    // Сообщение, пережившее перерисовку, отвечает на вопрос, которого уже никто
    // не задавал: снимается оно тем же одним движением, что и всё остальное.
    notice.textContent = '';

    if (state !== null) {
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    role.textContent =
      layers.role === null ? '' : enumLabel(strings, roleStringKey(layers.role));

    fillList(grantsList, layers.grants, (chosen) => {
      void revokeGrant(chosen);
    });
    fillList(deniesList, layers.denies, (chosen) => {
      void revokeDeny(chosen);
    });

    element.replaceChildren(
      heading,
      role,
      notice,
      grantsHeading,
      grantsList,
      grantForm,
      deniesHeading,
      deniesList,
      denyForm,
    );
  }

  /**
   * Исход неудачного **чтения**: содержимого нет вовсе, и состояние
   * принадлежит экрану целиком — вместе с нераскрытием чужого объекта.
   */
  function readingFailed(error: unknown): void {
    state = screenStateFor({
      kind: 'failed',
      code: error instanceof ApiFailure ? error.code : null,
    });
    render();
  }

  /**
   * Исход неудачного **действия**: экран уже показан, и уносить его нельзя.
   *
   * Два исхода всё же остаются за экраном, и оба названы. Отказ **без конверта
   * контракта** — обрыв связи, стоковая страница фреймворка, посредник — не
   * принёс ни кода, ни текста: сказать человеку на месте нечего, и молчание
   * оставило бы нажатие без последствий. Отказ прав и отсутствие объекта дают
   * «нет доступа», и текст сервера там показывать нельзя: он раскрыл бы ровно
   * то, что состояние скрывает (`FR-WEB-006`).
   *
   * Что это за исход, решает **единственное место соответствия** (решение
   * `D121`), а не второй перечень кодов рядом: копия перечня разошлась бы с
   * оригиналом молча.
   */
  function actionFailed(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const asScreen = screenStateFor({
      kind: 'failed',
      code: failure === null ? null : failure.code,
    });

    if (failure === null || failure.code === null || asScreen === 'noAccess') {
      state = asScreen;
      render();

      return;
    }

    // Осмысленный отказ: слои остаются теми, что пришли последним удавшимся
    // ответом, — отказ слоёв не приносит и подставить нажатое право было бы
    // показом того, чего сервер не подтверждал. Сказанное сервером идёт на
    // экран дословно: правило знает он, и пересказ его своими словами был бы
    // правилом в клиенте.
    render();

    const texts: string[] = [];

    for (const detail of failure.details) {
      texts.push(detail.message);
    }

    if (texts.length === 0 && failure.reason !== null) {
      texts.push(failure.reason);
    }

    notice.textContent = texts.join(' ');
  }

  /** Слои ставятся ответом целиком: показанным обязано быть пришедшее. */
  function applyAnswer(answer: MemberPermissions): void {
    layers = answer;
    state = null;
    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    layers = EMPTY_LAYERS;
    render();

    try {
      applyAnswer(await client.listMemberPermissions(companyId, membershipId));
    } catch (error) {
      layers = EMPTY_LAYERS;
      readingFailed(error);
    }
  }

  async function reload(): Promise<void> {
    try {
      applyAnswer(await client.listMemberPermissions(companyId, membershipId));
    } catch (error) {
      readingFailed(error);
    }
  }

  async function grant(right: Permission): Promise<void> {
    // Повтор выдачи клиентом не подавляется: идемпотентность живёт на сервере,
    // и своё правило поверх серверного было бы вторым правилом.
    try {
      applyAnswer(await client.grantMemberPermission(companyId, membershipId, right));
    } catch (error) {
      actionFailed(error);
    }
  }

  async function revokeGrant(right: Permission): Promise<void> {
    try {
      await client.revokeMemberGrant(companyId, membershipId, right);
    } catch (error) {
      actionFailed(error);

      return;
    }

    await reload();
  }

  async function deny(right: Permission): Promise<void> {
    try {
      applyAnswer(await client.denyMemberPermission(companyId, membershipId, right));
    } catch (error) {
      actionFailed(error);
    }
  }

  async function revokeDeny(right: Permission): Promise<void> {
    try {
      await client.revokeMemberDeny(companyId, membershipId, right);
    } catch (error) {
      actionFailed(error);

      return;
    }

    await reload();
  }

  grantForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void grant(grantChoice.value as Permission);
  });

  denyForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void deny(denyChoice.value as Permission);
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get permissions(): RightsLayers {
      return layers;
    },

    get offeredPermissions(): readonly Permission[] {
      return PERMISSIONS;
    },

    load,
    grant,
    revokeGrant,
    deny,
    revokeDeny,
  };
}
